import { Fragment, FunctionComponent, ReactNode } from "react";
import { IconButtonSkeleton } from "@App/Components/common/IconButton/IconButtonSkeleton";
import { IconButton } from "@App/Components/common/IconButton/IconButton";
import { Organization, Service } from "@App/types";
import { useTranslation } from "react-i18next";
import {
  GlassIcon,
  InspectionIcon,
  MiscIcon,
  TireSparkleIcon,
  VehicleDeliveryIcon,
  WheelchangeIcon,
} from "@App/Components/common/Icons";
import {
  OrganizationIsDBConnect,
  OrganizationIsMeinAuto,
} from "@App/utils/conditionals/organization";
import {
  OrganizationIsAyvensAndServiceIsEVehicleAppointment,
  OrganizationIsMobilityConceptAndServiceIsAutoAbo,
  ServiceIsWheelChangeAndOrganizationIsMeinAuto,
  ServiceIsWheelChangeAndOrganizationIsNio,
  ServiceIsWheelReplacementAndOrganizationIsMeinAuto,
  ServiceIsWheelReplacementAndOrganizationIsMobilityConcept,
} from "@App/utils/conditionals/combinations";
import {
  ServiceIsTireStorage,
  ServiceIsWheelChangeTiresStored,
} from "@App/utils/conditionals/services";

type ServiceStepProps = {
  service: Service | null;
  services: Service[];
  loading: boolean;
  error: any;
  selectService: any;
  organization: Organization;
};

export const ServiceStep: FunctionComponent<ServiceStepProps> = (props: ServiceStepProps) => {
  const { t } = useTranslation();

  const getIcon = (name: string): ReactNode => {
    const icons: Record<string, ReactNode> = {
      Inspection: <InspectionIcon />,
      Wheelchange: <WheelchangeIcon />,
      Glas: <GlassIcon />,
      Misc: <MiscIcon />,
      VehicleDelivery: <VehicleDeliveryIcon />,
    };

    return icons[name] ?? <MiscIcon />;
  };

  return (
    <Fragment>
      {props.loading && (
        <div className="overflow-hidden">
          <div
            className={`md:w-fit ease-in-out grid grid-cols-1 xs:grid-cols-2 gap-2 mb-4 mt-4 w-full`}
          >
            <IconButtonSkeleton />
            <IconButtonSkeleton />
            <IconButtonSkeleton />
            <IconButtonSkeleton />
          </div>
        </div>
      )}
      {props.error && <div>{t("error")}</div>}
      {!props.loading && props.services && (
        <div className="overflow-hidden">
          <div
            className={`md:w-fit ease-in-out grid grid-cols-1 xs:grid-cols-2 gap-2 mb-4 mt-4 w-full ${OrganizationIsDBConnect(props.organization) ? "lg:grid-cols-3 xl:grid-cols-4" : ""}`}
          >
            {props.services.map(
              (service) =>
                !OrganizationIsMobilityConceptAndServiceIsAutoAbo(props.organization, service) &&
                !OrganizationIsAyvensAndServiceIsEVehicleAppointment(props.organization, service) &&
                !ServiceIsWheelChangeTiresStored(service) &&
                !ServiceIsTireStorage(service) &&
                !ServiceIsWheelChangeAndOrganizationIsNio(props.organization, service) &&
                !ServiceIsWheelChangeAndOrganizationIsMeinAuto(props.organization, service) &&
                !ServiceIsWheelReplacementAndOrganizationIsMeinAuto(props.organization, service) &&
                !ServiceIsWheelReplacementAndOrganizationIsMobilityConcept(
                  props.organization,
                  service,
                ) && (
                  <IconButton
                    key={service.id}
                    title={service.pivot.display_name ? service.pivot.display_name : service.name}
                    onClick={() => props.selectService(service, true)}
                    subtitle={service.pivot.subtitle}
                    icon={getIcon(service?.pivot?.icon ?? "Misc")}
                  />
                ),
            )}
            {props.services.map(
              (service) =>
                (ServiceIsWheelChangeAndOrganizationIsNio(props.organization, service) ||
                  ServiceIsWheelChangeAndOrganizationIsMeinAuto(props.organization, service)) && (
                  <IconButton
                    key={service.id}
                    title={
                      OrganizationIsMeinAuto(props.organization)
                        ? "Wintertaugliche Bereifung"
                        : "Saisonaler Reifenwechsel"
                    }
                    onClick={() => props.selectService(service, true)}
                    subtitle={service.pivot.subtitle}
                    icon={<TireSparkleIcon primary className={"w-10 h-10"} />}
                  />
                ),
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
};
